// @flow
import { handleActions } from "redux-actions";

import {
  LOADING,
  PENDING,
  LOADED,
  SUCCESS,
  ERROR,
  NONE,
} from "shared/constants/status";

import type {
  selectOption,
  apiErrorReturn,
  apiErrorAction,
} from "shared/constants/flowTypes";

import type {
  receiveInstructorLocationsAction,
  receiveEventContentAction,
  receiveTemplatesAction,
  requestPostCabelasClassesAction,
  postCabelasClassesRowResultAction,
  CabelasClassBatchEvent,
  CabelasClassBatchResult,
} from "./createCabelasClassActionCreators";

import {
  parseEventContent,
  parseEventContentOptions,
  parseTemplates,
} from "./parsers/eventLookups";
import { parseLocationOptions, parseLocationsById } from "./parsers/locations";
import type { BusinessLocationRecord } from "./records/BusinessLocationRecord";

import {
  INSTRUCTOR_LOCATIONS_REQUEST,
  INSTRUCTOR_LOCATIONS_SUCCESS,
  INSTRUCTOR_LOCATIONS_ERROR,
  EVENT_CONTENT_REQUEST,
  EVENT_CONTENT_REQUEST_SUCCESS,
  EVENT_CONTENT_REQUEST_ERROR,
  TEMPLATES_REQUEST,
  TEMPLATES_REQUEST_SUCCESS,
  TEMPLATES_REQUEST_ERROR,
  POST_CABELAS_CLASSES_REQUEST,
  POST_CABELAS_CLASSES_ROW_RESULT,
  POST_CABELAS_CLASS_SUCCESS,
  POST_CABELAS_CLASS_ERROR,
  TIMEZONE_LOOKUP_REQUEST,
  TIMEZONE_LOOKUP_SUCCESS,
  TIMEZONE_LOOKUP_ERROR,
  TIMEZONE_LOOKUP_CLEAR,
} from "./createCabelasClassActions";

export type State = {
  eventContent: {},
  eventContentOptions: Array<selectOption>,
  eventContentReceivedAt: number,
  postCabelasClassErrorMessage: string,
  postCabelasClassErrors: apiErrorReturn,
  postCabelasClassStatus: string,
  postCabelasClassProgress: { created: number, total: number },
  postCabelasClassResults: Array<CabelasClassBatchResult>,
  requestEventContentErrorMessage: string,
  requestEventContentErrors: apiErrorReturn,
  requestEventContentStatus: string,
  requestTemplatesErrorMessage: string,
  requestTemplatesErrors: apiErrorReturn,
  requestTemplatesStatus: string,
  templates: {},
  templatesReceivedAt: number,
  instructorLocationsById: { [string]: BusinessLocationRecord },
  instructorLocationOptions: Array<selectOption>,
  requestInstructorLocationsStatus: string,
  requestTimezoneLookupStatus: string,
  requestTimezoneLookupErrorMessage: string,
};

const sortResults = (
  results: Array<CabelasClassBatchResult>
): Array<CabelasClassBatchResult> =>
  [...results].sort(
    (a: CabelasClassBatchResult, b: CabelasClassBatchResult): number =>
      a.batchIndex - b.batchIndex
  );

const countCreated = (results: Array<CabelasClassBatchResult>): number =>
  results.filter(
    (result: CabelasClassBatchResult): boolean => result.isCreated
  ).length;

const initialState: State = {
  requestEventContentStatus: NONE,
  requestEventContentErrorMessage: "",
  requestEventContentErrors: [],
  eventContent: {},
  eventContentOptions: [],
  eventContentReceivedAt: 0,
  requestTemplatesStatus: NONE,
  requestTemplatesErrorMessage: "",
  requestTemplatesErrors: [],
  templates: {},
  templatesReceivedAt: 0,
  postCabelasClassStatus: NONE,
  postCabelasClassProgress: { created: 0, total: 0 },
  postCabelasClassResults: [],
  postCabelasClassErrorMessage: "",
  postCabelasClassErrors: [],
  instructorLocationsById: {},
  instructorLocationOptions: [],
  requestInstructorLocationsStatus: NONE,
  requestTimezoneLookupStatus: NONE,
  requestTimezoneLookupErrorMessage: "",
};

const createCabelasClass = handleActions(
  {
    [EVENT_CONTENT_REQUEST]: (state: State): State => ({
      ...state,
      requestEventContentStatus: LOADING,
      requestEventContentErrorMessage: "",
      requestEventContentErrors: [],
      eventContent: {},
      eventContentOptions: [],
      eventContentReceivedAt: 0,
    }),
    [EVENT_CONTENT_REQUEST_SUCCESS]: (
      state: State,
      action: receiveEventContentAction
    ): State => {
      const {
        payload: { data },
        meta: { receivedAt },
      } = action;

      return {
        ...state,
        requestEventContentStatus: LOADED,
        eventContent: parseEventContent(data),
        eventContentOptions: parseEventContentOptions(data),
        eventContentReceivedAt: receivedAt,
      };
    },
    [EVENT_CONTENT_REQUEST_ERROR]: (
      state: State,
      action: apiErrorAction
    ): State => {
      const {
        payload: { errorMessage, errors },
      } = action;

      return {
        ...state,
        requestEventContentStatus: ERROR,
        requestEventContentErrorMessage: errorMessage,
        requestEventContentErrors: errors,
      };
    },
    [TEMPLATES_REQUEST]: (state: State): State => ({
      ...state,
      requestTemplatesStatus: LOADING,
      requestTemplatesErrorMessage: "",
      requestTemplatesErrors: [],
      templates: {},
      templatesReceivedAt: 0,
    }),
    [TEMPLATES_REQUEST_SUCCESS]: (
      state: State,
      action: receiveTemplatesAction
    ): State => {
      const {
        payload: { data },
        meta: { receivedAt },
      } = action;

      return {
        ...state,
        requestTemplatesStatus: LOADED,
        templates: parseTemplates(data),
        templatesReceivedAt: receivedAt,
      };
    },
    [TEMPLATES_REQUEST_ERROR]: (
      state: State,
      action: apiErrorAction
    ): State => {
      const {
        payload: { errorMessage, errors },
      } = action;

      return {
        ...state,
        requestTemplatesStatus: ERROR,
        requestTemplatesErrorMessage: errorMessage,
        requestTemplatesErrors: errors,
      };
    },
    [INSTRUCTOR_LOCATIONS_REQUEST]: (state: State): State => ({
      ...state,
      instructorLocationsById: {},
      instructorLocationOptions: [],
      requestInstructorLocationsStatus: LOADING,
    }),
    [INSTRUCTOR_LOCATIONS_SUCCESS]: (
      state: State,
      { payload: { locations } }: receiveInstructorLocationsAction
    ): State => ({
      ...state,
      instructorLocationsById: parseLocationsById(locations),
      instructorLocationOptions: parseLocationOptions(locations),
      requestInstructorLocationsStatus: LOADED,
    }),
    [INSTRUCTOR_LOCATIONS_ERROR]: (state: State): State => ({
      ...state,
      instructorLocationsById: {},
      instructorLocationOptions: [],
      requestInstructorLocationsStatus: ERROR,
    }),
    [TIMEZONE_LOOKUP_REQUEST]: (state: State): State => ({
      ...state,
      requestTimezoneLookupStatus: LOADING,
      requestTimezoneLookupErrorMessage: "",
    }),
    [TIMEZONE_LOOKUP_SUCCESS]: (state: State): State => ({
      ...state,
      requestTimezoneLookupStatus: SUCCESS,
      requestTimezoneLookupErrorMessage: "",
    }),
    [TIMEZONE_LOOKUP_ERROR]: (state: State, action: apiErrorAction): State => ({
      ...state,
      requestTimezoneLookupStatus: ERROR,
      requestTimezoneLookupErrorMessage: action.payload.errorMessage,
    }),
    [TIMEZONE_LOOKUP_CLEAR]: (state: State): State => ({
      ...state,
      requestTimezoneLookupStatus: NONE,
      requestTimezoneLookupErrorMessage: "",
    }),
    [POST_CABELAS_CLASSES_REQUEST]: (
      state: State,
      { payload: { events, isRetry } }: requestPostCabelasClassesAction
    ): State => {
      const retriedIndexes = events.map(
        (batchEvent: CabelasClassBatchEvent, index: number): number =>
          batchEvent.batchIndex ?? index
      );

      return {
        ...state,
        postCabelasClassStatus: PENDING,
        postCabelasClassErrorMessage: "",
        postCabelasClassErrors: [],
        postCabelasClassProgress: isRetry
          ? state.postCabelasClassProgress
          : { created: 0, total: events.length },
        postCabelasClassResults: isRetry
          ? state.postCabelasClassResults.filter(
              (result: CabelasClassBatchResult): boolean =>
                !retriedIndexes.includes(result.batchIndex)
            )
          : [],
      };
    },
    [POST_CABELAS_CLASSES_ROW_RESULT]: (
      state: State,
      { payload: { result } }: postCabelasClassesRowResultAction
    ): State => ({
      ...state,
      postCabelasClassResults: sortResults([
        ...state.postCabelasClassResults.filter(
          (existing: CabelasClassBatchResult): boolean =>
            existing.batchIndex !== result.batchIndex
        ),
        result,
      ]),
      postCabelasClassProgress: {
        ...state.postCabelasClassProgress,
        created: countCreated([
          ...state.postCabelasClassResults.filter(
            (existing: CabelasClassBatchResult): boolean =>
              existing.batchIndex !== result.batchIndex
          ),
          result,
        ]),
      },
    }),
    [POST_CABELAS_CLASS_SUCCESS]: (state: State): State => ({
      ...state,
      postCabelasClassStatus: SUCCESS,
    }),
    [POST_CABELAS_CLASS_ERROR]: (
      state: State,
      action: apiErrorAction
    ): State => {
      const {
        payload: { errorMessage, errors },
      } = action;

      return {
        ...state,
        postCabelasClassStatus: ERROR,
        postCabelasClassErrorMessage: errorMessage,
        postCabelasClassErrors: errors,
      };
    },
  },
  initialState
);

export default createCabelasClass;
